import React, { useState } from 'react';
import { getPlanPricing, getPlanDisplayName } from '../services/subscriptionService';
import SubscriptionDisabledModal from './SubscriptionDisabledModal';

const PricingTiers = ({ 
    currentPlan = 'free', 
    onSelectPlan, 
    loading = false, 
    showCurrentPlan = false,
    className = '' 
}) => {
    const [showDisabledModal, setShowDisabledModal] = useState(false);
    const plans = ['free', 'basic', 'unlimited'];
    
    const getPlanButtonText = (planType) => {
        if (planType === currentPlan && showCurrentPlan) {
            return 'Current Plan';
        }
        
        switch (planType) {
            case 'free':
                return 'Get Started';
            case 'basic':
                return 'Upgrade to Basic';
            case 'unlimited':
                return 'Upgrade to Unlimited';
            default:
                return 'Select Plan';
        }
    };

    const getPlanButtonClass = (planType) => {
        const baseClass = "px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none";
        
        if (planType === currentPlan && showCurrentPlan) {
            return `${baseClass} bg-neutral-50 text-neutral-900 hairline cursor-default`;
        }
        
        // Disable all paid plans
        if (planType === 'basic' || planType === 'unlimited') {
            return `${baseClass} bg-neutral-300 text-neutral-500 cursor-not-allowed`;
        }
        
        return `${baseClass} bg-[#1b263b] text-white hover:opacity-60`;
    };

    const isButtonDisabled = (planType) => {
        return loading || (planType === currentPlan && showCurrentPlan) || (planType === 'basic' || planType === 'unlimited');
    };

    const handlePlanClick = (planType) => {
        if (planType === 'basic' || planType === 'unlimited') {
            setShowDisabledModal(true);
            return;
        }
        onSelectPlan(planType);
    };

    return (
        <>
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${className}`}>
                {plans.map((planType) => {
                    const pricing = getPlanPricing(planType);
                    const planName = getPlanDisplayName(planType);
                    const isCurrent = planType === currentPlan && showCurrentPlan;
                    const isPopular = planType === 'basic';

                    return (
                        <div 
                            key={planType}
                            className={`bg-white p-6 hairline relative ${
                                isPopular ? 'ring-2 ring-[#1b263b]' : ''
                            }`}
                        >
                            {/* Popular Badge */}
                            {isPopular && (
                                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                    <span className="bg-[#1b263b] text-white px-3 py-1 text-xs font-mono uppercase tracking-wider">
                                        Most Popular
                                    </span>
                                </div>
                            )}

                            {/* Plan Header */}
                            <div className="text-center mb-6">
                                <h3 className="font-sans text-xl font-medium text-[#1b263b] mb-2">
                                    {planName}
                                </h3>
                                <div className="mb-4">
                                    {pricing.price === 0 ? (
                                        <span className="font-sans text-3xl font-medium text-[#1b263b]">Free</span>
                                    ) : (
                                        <div>
                                            <span className="font-sans text-3xl font-medium text-[#1b263b]">
                                                ${pricing.price}
                                            </span>
                                            <span className="font-sans text-base text-neutral-600 ml-1">
                                                /{pricing.period}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Recipe Count */}
                                <div className="mb-4">
                                    {pricing.recipes === -1 ? (
                                        <span className="font-mono text-sm uppercase tracking-wider text-[#1b263b]">
                                            Unlimited Recipes
                                        </span>
                                    ) : (
                                        <span className="font-mono text-sm uppercase tracking-wider text-[#1b263b]">
                                            {pricing.recipes} Recipe{pricing.recipes !== 1 ? 's' : ''}
                                            {planType === 'basic' && ' / Month'}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Features */}
                            <div className="mb-6">
                                <ul className="space-y-2">
                                    {pricing.features.map((feature, index) => (
                                        <li key={index} className="flex items-start">
                                            <span className="text-[#1b263b] mr-2">✓</span>
                                            <span className="font-sans text-sm text-neutral-900">
                                                {feature}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Action Button */}
                            <div className="text-center">
                                <button
                                    onClick={() => handlePlanClick(planType)}
                                    disabled={isButtonDisabled(planType)}
                                    className={getPlanButtonClass(planType)}
                                    style={{ 
                                        backgroundColor: planType === 'unlimited' ? '#1b263b' : 
                                                       isCurrent ? '#F9F8F6' : 
                                                       (planType === 'basic' || planType === 'unlimited') ? '#d1d5db' : '#1b263b',
                                        color: isCurrent ? '#0D0D0D' : 
                                               (planType === 'basic' || planType === 'unlimited') ? '#6b7280' : '#FFFFFF'
                                    }}
                                >
                                    {loading ? 'Processing...' : 
                                     (planType === 'basic' || planType === 'unlimited') ? 'Coming Soon' : 
                                     getPlanButtonText(planType)}
                                </button>
                            </div>

                            {/* Current Plan Indicator */}
                            {isCurrent && (
                                <div className="mt-4 text-center">
                                    <span className="font-mono text-xs uppercase tracking-wider text-neutral-600">
                                        Your Current Plan
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Disabled Modal */}
            <SubscriptionDisabledModal 
                isOpen={showDisabledModal}
                onClose={() => setShowDisabledModal(false)}
            />
        </>
    );
};

export default PricingTiers;
